import React, { Component, MouseEvent } from "react";
import classnames from "classnames";
import equal from "fast-deep-equal/es6";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { v4 as uuid } from "uuid";

import { combineFilters } from "@formant/core/common/combineFilters";
import { defined } from "@formant/core/common/defined";
import { distinct } from "@formant/core/common/distinct";
import { isFilterEmpty } from "@formant/core/common/isFilterEmpty";
import { AddableModuleType } from "@formant/core/model/AddableModuleType";
import { IAnalyticsLayout } from "@formant/core/model/IAnalyticsLayout";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { IComment } from "@formant/core/model/IComment";
import { IDevice } from "@formant/core/model/IDevice";
import { IEvent } from "@formant/core/model/IEvent";
import { IFilter } from "@formant/core/model/IFilter";
import { IMetadata } from "@formant/core/model/IMetadata";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { IPartialViewConfiguration } from "@formant/core/model/IPartialViewConfiguration";
import { IScopeFilter } from "@formant/core/model/IScopeFilter";
import { IShare } from "@formant/core/model/IShare";
import { ITeleopLayout } from "@formant/core/model/ITeleopLayout";
import { IViewConfiguration } from "@formant/core/model/IViewConfiguration";
import { ModuleMode } from "@formant/core/model/ModuleMode";
import { ModuleType } from "@formant/core/model/ModuleType";
import ReactGridLayout, {
    type Layout as GridLayout,
} from "@formant/react-grid-layout";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";
import { Measure } from "@formant/ui/component/common/Measure";

import "@formant/react-grid-layout/css/styles.css";
import "@formant/react-resizable/css/styles.css";

import { track } from "../../analytics";
import {
    deviceConfigurationStore,
    metadataStore,
    observeStore,
    onlineDevicesStore,
    streamStore,
} from "../../stores";
import { Breakpoint, getBreakpoint } from "../../utils/breakpoint";
import {
    columns,
    generateCompleteLayout,
    IModuleInfo,
} from "../../utils/layoutHelpers";
import { roundToWholeNumber } from "../../utils/roundToWholeNumber";
import ClickSignal from "../Teleop/ClickSignal";
import { GridModule, IGridModuleProps } from "./GridModule";
import { ModuleLimitExceeded } from "./ModuleLimitExceeded";
import * as styles from "./Modules.scss";
import { NoResults } from "./NoResults";

const margin = 10;
const aspect = 4 / 3;
const moduleLimit = 100;

export interface IModulesProps {
    context: IFilter;
    filter?: IFilter;
    layout: IObserveLayout | ITeleopLayout | IAnalyticsLayout;
    configuration: IViewConfiguration[];
    device?: IDevice;
    selectedTime?: Date;
    activeEvent?: IEvent;
    editingLayout?: boolean;
    focusedModule?: [string, AddableModuleType?];
    fullscreen?: boolean;
    noGrid?: boolean;
    onSelectTime?: (time: Date) => void;
    onSeek?: (time: Date) => void;
    onChangeLayout?: (layout: IObserveLayout) => void;
    onChangeFilter?: (filter: IFilter) => void;
    onChangeConfiguration?: (value: IPartialViewConfiguration) => void;
    onFocusModule?: (stream?: [string, AddableModuleType?]) => void;
    onToggleFullscreen?: (stream?: [string, AddableModuleType?]) => void;
    onComment?: (comment: Partial<IComment>) => void;
    onAnnotate?: (annotation: Partial<IAnnotation>) => void;
    onDemandData?: (streamName: string) => void;
    onConfigureModule?: (moduleId: string) => void;
    onRemoveModule?: (moduleid: string) => void;
    analyticsOnly?: boolean;
    hideDevice: boolean;
    share?: IShare;
    moduleMode: ModuleMode;
}

@observer
export class Modules extends Component<IModulesProps> {
    @observable
    private width: number = 0;
    @observable
    private breakpoint: Breakpoint = "xl";
    private lastMetadata: IMetadata[] = [];
    @observable
    addingModule: boolean = false;
    uiSettled: boolean = false;

    public componentDidMount() {
        observeStore.arrangingModules = false;
        setTimeout(() => {
            this.uiSettled = true;
        }, 1000);
    }

    public render() {
        const {
            configuration,
            device,
            layout,
            filter,
            activeEvent,
            editingLayout,
            focusedModule,
            fullscreen,
            noGrid,
            onSelectTime,
            onSeek,
            onFocusModule,
            onToggleFullscreen,
            onChangeConfiguration,
            onChangeFilter,
            onComment,
            onAnnotate,
            onDemandData,
            hideDevice,
            share,
            onConfigureModule,
            onRemoveModule,
            moduleMode,
            analyticsOnly,
        } = this.props;

        const navigatorEnabled = false;
        const onlineDevices = onlineDevicesStore.online();

        const { breakpoint, onBlur } = this;
        const {
            metadataStart,
            metadataEnd,
            configuringModule,
            hideInactiveStreams,
            timescaleMetadataStart,
            timescaleMetadataEnd,
            moduleToAdd,
        } = observeStore;

        if (moduleToAdd) {
            this.addModule();
        } else {
            this.addingModule = false;
        }

        const deviceConfiguration = device?.id
            ? deviceConfigurationStore.get(device.id)
            : undefined;

        const filtering = filter && !isFilterEmpty(filter);

        const start = hideInactiveStreams
            ? timescaleMetadataStart.toISOString()
            : metadataStart.toISOString();
        const end = hideInactiveStreams
            ? timescaleMetadataEnd.toISOString()
            : metadataEnd.toISOString();

        const context: IScopeFilter = {
            ...this.props.context,
            start,
            end,
        };
        const mergedFilter: IScopeFilter = combineFilters(context, {
            ...filter,
            deviceIds: undefined,
        });

        const queryMetadata = metadataStore.list(mergedFilter);

        const isDeviceOffline =
            onlineDevices && device?.id && !onlineDevices.has(device?.id);
        if (isDeviceOffline) {
            const cachedMetadata =
                queryMetadata && queryMetadata.length > 0
                    ? queryMetadata
                    : this.lastMetadata;
            const hasHistoricalData =
                cachedMetadata && cachedMetadata.length > 0;
            if (!hasHistoricalData) {
                return <NoResults offline={true} />;
            }
        }

        const loading = queryMetadata === undefined;

        this.lastMetadata = queryMetadata || this.lastMetadata;
        const metadata = this.lastMetadata;

        let activeStreamInfos = generateStreamInfo(metadata);
        activeStreamInfos = activeStreamInfos.filter(
            (streamInfo, index, self) =>
                index === self.findIndex(t => t.name === streamInfo.name),
        );

        if (analyticsOnly) {
            activeStreamInfos = [];
        }

        const activeStreams = new Set(activeStreamInfos.map(_ => _.name));
        const streamNameTypeMap = streamStore.streamNameTypeMap;
        const layoutStreamInfos: IModuleInfo[] =
            editingLayout || filtering
                ? []
                : Object.keys(layout)
                      .filter(
                          _ =>
                              !activeStreams.has(_) &&
                              streamNameTypeMap?.has(_),
                      )
                      .map(name => ({
                          name,
                          type: defined(streamNameTypeMap?.get(name)),
                      }));

        const customStreamInfos = Object.entries(layout)
            .filter(([name, _]) => name.startsWith("custom-module:"))
            .map(([name, _]) => ({
                name,
                type: _.configuration?.moduleType || ("custom" as ModuleType),
            }));

        const controls = Object.entries(layout)
            .filter(([name, _]) => name.endsWith("controls"))
            .map(([name, _]) => ({
                name,
                type: name as ModuleType,
            }));

        const analyticsInfos = Object.entries(layout)
            .filter(([name, _]) => name.startsWith("analytics-module:"))
            .map(([name, _]) => ({
                name,
                type:
                    _.configuration?.moduleType || ("analytics" as ModuleType),
            }));

        const streamInfos = hideInactiveStreams
            ? [...activeStreamInfos, ...customStreamInfos, ...analyticsInfos]
            : [
                  ...activeStreamInfos,
                  ...layoutStreamInfos,
                  ...customStreamInfos,
                  ...analyticsInfos,
                  ...controls,
              ];

        const moduleLimitExceeded = streamInfos.length > moduleLimit;
        const visibleStreamInfos = streamInfos.slice(0, moduleLimit);

        const isOnDemand = new Set(
            (
                (deviceConfiguration &&
                    deviceConfiguration.document.telemetry?.streams) ||
                []
            )
                .filter(_ => _.onDemand)
                .map(_ => _.name),
        );

        const videoStreamTypeMap: Record<string, AddableModuleType[]> = {};

        visibleStreamInfos.forEach(stream => {
            const { name, type } = stream;
            if (!videoStreamTypeMap[type]) {
                videoStreamTypeMap[type] = [];
            }
            videoStreamTypeMap[type].push(name as AddableModuleType);
        });

        const hiddenModuleNames = mergedFilter?.notNames || [];
        const visibleModules = visibleStreamInfos
            .filter(_ => !hiddenModuleNames.includes(_.name))
            .map(({ name: streamName, type: streamType }) => {
                const layoutModule = layout[streamName];
                const moduleProps: IGridModuleProps = {
                    streamName,
                    streamNames: videoStreamTypeMap[streamType],
                    streamType,
                    device,
                    onDemand: isOnDemand.has(streamName),
                    filter: mergedFilter,
                    activeEvent,
                    focused: focusedModule?.[0] === streamName,
                    fullscreen: focusedModule?.[0] === streamName && fullscreen,
                    editingLayout,
                    noGrid,
                    configuration,
                    onSelectTime,
                    onSeek,
                    onFocus: onFocusModule,
                    onBlur,
                    onToggleFullscreen,
                    onChangeConfiguration,
                    onComment,
                    onAnnotate,
                    onDemandData,
                    onRemoveModule,
                    hideDevice,
                    share,
                    layoutModule,
                    onConfigureModule,
                    moduleMode,
                };

                return (
                    <div key={streamName}>
                        <GridModule {...moduleProps} />
                    </div>
                );
            });

        const visibleLayout = generateCompleteLayout(
            layout,
            visibleStreamInfos.map(_ => ({
                name: _.name,
                type: _.type,
            })),
        );

        const rowHeight =
            (this.width - (columns + 1) * margin) / columns / aspect;

        const measuring = this.width === 0;

        const visible = !measuring && streamInfos.length > 0;

        let canDrag = !configuringModule;
        if (!this.props.onChangeLayout) {
            canDrag = false;
        }
        return (
            <>
                <div
                    className={classnames(styles.modules)}
                    onClick={this.onClick}
                >
                    <Measure
                        className={classnames(
                            styles.grid,
                            visible && styles.visible,
                        )}
                        onMeasureResize={this.onResize}
                    >
                        {!measuring && (
                            <ReactGridLayout
                                width={this.width}
                                margin={[margin, 2 * margin]}
                                cols={columns}
                                rowHeight={rowHeight}
                                layout={buildGridLayout(
                                    visibleLayout,
                                    breakpoint,
                                    !!editingLayout,
                                )}
                                isDraggable={canDrag}
                                draggableCancel=".preventDrag"
                                isResizable={canDrag}
                                onResizeStart={this.onChangeStart}
                                onDragStop={this.onChangeStop}
                                onResizeStop={this.onChangeStop}
                                onLayoutChange={(gridLayout: GridLayout[]) =>
                                    this.onLayoutChange(gridLayout)
                                }
                                onDrag={this.onChangeStart}
                                draggableHandle={
                                    !editingLayout ? ".dragHandle" : undefined
                                }
                                useCSSTransforms={false}
                                compactType={
                                    moduleMode === ModuleMode.Observe
                                        ? "vertical"
                                        : null
                                }
                            >
                                {visibleModules}
                            </ReactGridLayout>
                        )}
                    </Measure>
                    <LoadingIndicator
                        visible={loading}
                        className={styles.loading}
                    />
                    {!loading &&
                        streamInfos.length === 0 &&
                        !navigatorEnabled &&
                        moduleMode === ModuleMode.Observe && (
                            <NoResults
                                filter={filter}
                                onChangeFilter={onChangeFilter}
                            />
                        )}
                    {moduleLimitExceeded && <ModuleLimitExceeded />}
                </div>
            </>
        );
    }

    addModule = () => {
        if (this.addingModule === false) {
            this.addingModule = true;
            window.setTimeout(() => {
                // add module to bottom of page
                if (observeStore.moduleToAdd) {
                    const newId = uuid();
                    const newLayout: IObserveLayout = {
                        ["custom-module:" + newId]: {
                            x: 0,
                            y: 0,
                            width: 8,
                            height: 6,
                            configuration: {
                                customName: observeStore.moduleToAdd.name,
                                moduleType: "custom",
                                customVisualizationUrl:
                                    observeStore.moduleToAdd.url,
                            },
                        },
                    };
                    this.props.onChangeLayout?.(newLayout);
                }
                observeStore.exploring = false;
                observeStore.moduleToAdd = undefined;
            });
        }
    };

    private onChangeStart = () => {
        if (!observeStore.arrangingModules) {
            track("Arranging Modules: Start", { mode: this.props.moduleMode });
            observeStore.arrangingModules = true;
        }
    };

    private onChangeStop = () => {
        if (observeStore.arrangingModules) {
            track("Arranging Modules: Stop", { mode: this.props.moduleMode });
            observeStore.arrangingModules = false;
        }
    };

    private onResize = (width: number) => {
        this.breakpoint = getBreakpoint(width);
        this.width = width;
    };

    private onLayoutChange = (gridLayout: GridLayout[]) => {
        const { filter, editingLayout, layout, onChangeLayout } = this.props;

        if (observeStore.skipNextLayout) {
            observeStore.skipNextLayout = false;
            return;
        }

        if (!onChangeLayout) {
            return;
        }

        if (!editingLayout && this.breakpoint !== "xl") {
            return;
        }

        if (gridLayout.length === 0) {
            return;
        }

        const newLayout = gridLayout
            .map(({ i, x, y, w, h, static: newStatic }) => ({
                name: defined(i),
                x,
                y,
                width: w,
                height: h,
                static: newStatic,
            }))
            .filter(({ name }) => !(filter?.notNames || []).includes(name))
            .reduce<IObserveLayout>(
                (acc, { name, x, y, width, height, static: newStatic }) => {
                    const extra = layout[name]?.configuration
                        ? { configuration: layout[name]?.configuration }
                        : {};
                    acc[name] = {
                        x,
                        y,
                        width,
                        height,
                        static: newStatic,
                        ...extra,
                    };
                    return acc;
                },
                {},
            );

        if (equal(newLayout, layout)) {
            return;
        }

        if (this.uiSettled !== true) {
            return;
        }

        onChangeLayout(newLayout);
    };

    private onClick = (e: MouseEvent) => {
        if (this.props.moduleMode === ModuleMode.Teleop) {
            ClickSignal.sendClick({ clientX: e.clientX, clientY: e.clientY });
        }
        this.onBlur();
    };

    private onBlur = () => {
        const { fullscreen } = this.props;
        if (fullscreen) {
            return;
        }
        this.props.onFocusModule?.(undefined);
    };
}

export function generateStreamInfo(metadata: IMetadata[]): IModuleInfo[] {
    const streamNameTypeMap = streamStore.streamNameTypeMap;
    return distinct(metadata.map(_ => ({ name: _.name, type: _.type }))).reduce<
        IModuleInfo[]
    >((a, { name: streamName, type: metadataType }) => {
        const streamType = streamNameTypeMap?.get(streamName);
        const type = streamType || metadataType;
        return [
            ...a,
            {
                name: streamName,
                type,
                id: metadata.find(_ => _.name === streamName && _.type === type)
                    ?.id,
            },
        ];
    }, []);
}

function buildGridLayout(
    layout: IObserveLayout,
    breakpoint: Breakpoint,
    editing: boolean,
): GridLayout[] {
    if (editing || breakpoint === "xl") {
        return Object.keys(layout).map(name => {
            const { x, y, width, height, static: isStatic } = layout[name];
            const isAnalytics = name.includes("analytics-module:");

            return {
                i: name,
                x,
                y,
                w: width,
                h: height,
                minH: isAnalytics ? 3 : 1,
                minW: isAnalytics ? 3 : 1,
                static: isStatic || false,
            };
        });
    }

    let minW = 2;
    let minH = 2;

    if (breakpoint === "lg" || breakpoint === "md" || breakpoint === "sm") {
        minW = 6;
        minH = 4;
    }

    if (breakpoint === "xs") {
        minW = 12;
        minH = 8;
    }

    return Object.keys(layout).map(name => {
        const { x, y, width, height, static: isStatic } = layout[name];
        const w = roundToWholeNumber(Math.max(width, minW), minW);
        const h = roundToWholeNumber(Math.max(minH, height), minH);
        const adjustedX = roundToWholeNumber(x, minW);

        return {
            i: name,
            x: adjustedX,
            y,
            w,
            h,
            minH,
            minW,
            static: isStatic || false,
        };
    });
}
